import { initializeApp } from 'firebase/app'
import {
  getMessaging,
  getToken,
  onMessage,
  type Messaging,
  type MessagePayload
} from 'firebase/messaging'
import { api } from './api'

type PushPermissionResult = {
  granted: boolean
  token?: string
  error?: string
}

type FirebaseConfig = {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId?: string
}

const demoFirebaseConfig: FirebaseConfig = {
  apiKey: 'demo-api-key',
  authDomain: 'ngc-medical-demo.firebaseapp.com',
  projectId: 'ngc-medical-demo',
  storageBucket: 'ngc-medical-demo.appspot.com',
  messagingSenderId: '123456789012',
  appId: '1:123456789012:web:abcdef123456',
  measurementId: 'G-XXXXXXXXXX'
}

const resolveFirebaseConfig = (): FirebaseConfig => {
  if (typeof window !== 'undefined') {
    const injected = (window as Window & { __FIREBASE_CONFIG__?: FirebaseConfig }).__FIREBASE_CONFIG__
    if (injected?.apiKey && injected?.projectId) {
      return injected
    }
  }

  const jsonConfig = import.meta.env.VITE_FIREBASE_CONFIG
  if (jsonConfig) {
    try {
      return JSON.parse(jsonConfig) as FirebaseConfig
    } catch (error) {
      console.warn('Falha ao parsear VITE_FIREBASE_CONFIG, usando configuracao padrao.')
    }
  }

  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || demoFirebaseConfig.apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || demoFirebaseConfig.authDomain,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || demoFirebaseConfig.projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || demoFirebaseConfig.storageBucket,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || demoFirebaseConfig.messagingSenderId,
    appId: import.meta.env.VITE_FIREBASE_APP_ID || demoFirebaseConfig.appId,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || demoFirebaseConfig.measurementId
  }
}

const resolveVapidKey = (): string => {
  if (typeof window !== 'undefined') {
    const injected = (window as Window & { __FIREBASE_VAPID_KEY__?: string }).__FIREBASE_VAPID_KEY__
    if (injected) return injected
  }

  return import.meta.env.VITE_FIREBASE_VAPID_KEY || 'demo-vapid-key'
}

const firebaseConfig = resolveFirebaseConfig()
const VAPID_KEY = resolveVapidKey()

const app = initializeApp(firebaseConfig)
let messaging: Messaging | null = null
let swRegistration: ServiceWorkerRegistration | null = null

if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  try {
    messaging = getMessaging(app)
  } catch (error) {
    console.error('Falha ao inicializar Firebase Messaging:', error)
  }
}

const ensureServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return null

  if (swRegistration) return swRegistration

  try {
    swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
    return swRegistration
  } catch (error) {
    console.error('Falha ao registrar firebase-messaging-sw.js:', error)
    return null
  }
}

class PartnerPushNotificationsService {
  private currentToken: string | null = null

  isSupported(): boolean {
    return !!(
      messaging &&
      'Notification' in window &&
      'serviceWorker' in navigator
    )
  }

  getPermissionStatus(): NotificationPermission {
    return Notification.permission
  }

  async requestPermission(): Promise<PushPermissionResult> {
    if (!this.isSupported()) {
      return { granted: false, error: 'Push nao suportado neste navegador' }
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        return { granted: false, error: 'Permissao de notificacao negada' }
      }

      const registration = await ensureServiceWorker()
      if (!messaging) {
        return { granted: false, error: 'Firebase Messaging nao inicializado' }
      }

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration || undefined
      })

      if (!token) {
        return { granted: false, error: 'Nao foi possivel obter token do Firebase' }
      }

      this.currentToken = token
      return { granted: true, token }
    } catch (error) {
      console.error('Erro ao solicitar permissao de push:', error)
      return {
        granted: false,
        error: error instanceof Error ? error.message : 'Erro inesperado ao habilitar push'
      }
    }
  }

  async getCurrentToken(): Promise<string | null> {
    if (!messaging) return null

    try {
      if (this.currentToken) return this.currentToken

      const registration = await ensureServiceWorker()
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration || undefined
      })

      this.currentToken = token
      return token
    } catch (error) {
      console.error('Erro ao obter token atual de push:', error)
      return null
    }
  }

  async registerToken(token: string): Promise<boolean> {
    try {
      const deviceName = navigator.userAgent?.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser'
      await api.post('/partner-admin/notifications/device-token', {
        token,
        platform: 'web',
        device_name: deviceName,
        user_agent: navigator.userAgent
      })
      return true
    } catch (error) {
      console.error('Erro ao registrar token de push:', error)
      return false
    }
  }

  onMessage(callback: (payload: MessagePayload) => void): () => void {
    if (!messaging) return () => undefined

    return onMessage(messaging, (payload) => {
      try {
        callback(payload)
      } catch (error) {
        console.error('Erro ao processar mensagem push:', error)
      }
    })
  }
}

export const partnerPushNotifications = new PartnerPushNotificationsService()
export type { PushPermissionResult }
